import { useEffect, useState } from 'react';
import { useMedia } from 'react-use';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { tokens, Loader as Spinner } from '@hotjar/hotsauce';
import { FadeIn } from '@hotjar/webapp/utils/animations';
import type { Site } from '@hotjar/site';
import { useCurrentSite } from '@hotjar/site';
import { useWindowTitleWithBlur } from '@hotjar/webapp/hooks/useWindowTitle';
import { TopBar } from '@hotjar/webapp/features/Activation/TopBar';
import { getParameter } from '@hotjar/insights-common/lib/url';
import { hjGetCookie } from '@hotjar/webapp/utils/cookies';
import { InviteTeammateStep } from '@hotjar/webapp/features/Activation/SetupWizard/steps/InviteTeammateStep';
import { useAccountExperiment } from '@hotjar/webapp/hooks/useExperiment';
import { useSetupWizard } from './hooks/useSetupWizard';
import { SetupWizardHeader } from './SetupWizardHeader';
import {
  CMS,
  SiteUrlStep,
  InstallationMethodStep,
  IntegrationGuides,
  EmailTrackingCode,
  IntegrateWithCMS,
  IntegrateWithGTM,
  ManualInstallation,
  NPMInstallation,
  MobileOnlyStep,
  WideStep as Step,
  StepBox,
  StepHeader,
  STEPS,
} from './steps';
import { RowWrapper } from '../../Login/styles';

const MOBILE_BREAKPOINT = '(min-width: 960px)';
const LARGE_SCREEN = '(min-width: 1560px)';
const TOP_BAR_HEIGHT = '62px';

const StyledSpinner = styled(Spinner)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const Wrapper = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
`;

const TopBarWrapper = styled.div`
  position: fixed;
  width: 100vw;
  z-index: 2;
  border-bottom: 1px solid ${tokens.color.ui_border_light};
`;

const StyledRowWrapper = styled(RowWrapper)`
  margin-top: ${TOP_BAR_HEIGHT};
  justify-content: space-between;
  background-color: ${tokens.color.neutral_100};
  border-top: none;
`;

const Content = styled.section<{ wide?: boolean }>`
  display: flex;
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  box-sizing: content-box;
  align-items: flex-start;
  background-color: ${tokens.color.neutral_100};
  animation: ${FadeIn} 0.5s ease;
  align-items: center;
  padding: ${tokens.spacer.x3};

  @media ${MOBILE_BREAKPOINT} {
    padding: ${({ wide }) => (wide ? `0 ${tokens.spacer.x3}` : tokens.spacer.x3)};
  }

  @media ${LARGE_SCREEN} {
    flex-grow: 1;
    max-width: ${({ wide }) => (wide ? '1400px' : 'initial')};
  }
`;

const StyledStep = styled(Step)`
  width: 100%;
  max-width: 900px;
  margin: 0 auto;

  @media ${LARGE_SCREEN} {
    min-width: 900px;
  }
`;

export const SetupWizard = (): JSX.Element => {
  const [analysisAnimationFinished, setAnalysisAnimationFinished] = useState<boolean>();
  const { step, gtm, cms, isLoading, goToInsights, goToStep } = useSetupWizard();
  const { data: currentSite } = useCurrentSite();
  const onMobile = useMedia('(max-width: 960px)');
  const [selectedCMS, setSelectedCMS] = useState<CMS>();
  const { t } = useTranslation();
  const currentParamStep = getParameter(window.location.href, 'step');
  const isCurrentStepSiteUrl = currentParamStep === STEPS.SITE_URL;
  const HJ_WEBSITE_NEXT_LOCALE = 'NEXT_LOCALE';
  const locale = hjGetCookie(HJ_WEBSITE_NEXT_LOCALE);
  const PT_BR_LOCALE = 'pt-BR';
  const isLocalePtBr = !!(locale && [PT_BR_LOCALE].includes(locale));
  const mobileViewEnabled = onMobile && !isLocalePtBr;

  const { inVariant1: shouldShowInviteTeammateStep } = useAccountExperiment(
    'stp_invite_teammate_registration_step_v2',
    currentSite?.account,
    /* Don't render <InviteTeammateStep /> for mobile, there's a dedicated
     <EmailTrackingCodeMobile /> for small devices */
    !!currentSite?.account && !onMobile,
  );

  const onCMSClick = (clickedCMS: CMS) => {
    setSelectedCMS(clickedCMS);
    goToStep(STEPS.CMS);
  };
  const onSiteUrlStepSubmitted = () => {
    if (shouldShowInviteTeammateStep) {
      goToStep(STEPS.INVITE_TEAMMATE);
    } else {
      goToInsights();
    }
  };

  const onSiteUrlStepSkipped = () => {
    if (shouldShowInviteTeammateStep) {
      goToStep(STEPS.INVITE_TEAMMATE);
    } else {
      goToInsights();
    }
  };

  useWindowTitleWithBlur(
    t('activation:setup-wizard.page-title'),
    t('activation:setup-wizard.page-title'),
  );

  useEffect(() => {
    if (analysisAnimationFinished && step === STEPS.SITE_URL) {
      setAnalysisAnimationFinished(false);
      goToStep(mobileViewEnabled ? STEPS.MOBILE_LOCK : STEPS.INSTALLATION_METHOD);
    }
  }, [analysisAnimationFinished, step, goToStep, mobileViewEnabled]);

  useEffect(() => {
    const currentCms = selectedCMS || cms;
    if (step === STEPS.CMS && !Object.values(CMS).includes(currentCms as CMS)) {
      goToStep(STEPS.INSTALLATION_METHOD);
    }
  }, [cms, goToStep, selectedCMS, step]);

  useEffect(() => {
    if (mobileViewEnabled && step !== STEPS.MOBILE_LOCK) {
      goToStep(STEPS.MOBILE_LOCK);
    } else if (!mobileViewEnabled && step === STEPS.MOBILE_LOCK) {
      goToStep(currentSite?.url ? STEPS.INSTALLATION_METHOD : STEPS.SITE_URL);
    }
  }, [mobileViewEnabled, step, goToStep, currentSite?.url]);

  return (
    <Wrapper>
      <TopBarWrapper>
        {isCurrentStepSiteUrl || step === STEPS.MOBILE_LOCK ? (
          <TopBar />
        ) : (
          <SetupWizardHeader onSkip={goToInsights} />
        )}
      </TopBarWrapper>
      <StyledRowWrapper>
        <Content>
          {isLoading ? <StyledSpinner data-testid="loader" /> : null}
          {!isLoading && step === STEPS.MOBILE_LOCK ? (
            <MobileOnlyStep onSkip={goToInsights} />
          ) : null}
          {!isLoading && step === STEPS.SITE_URL ? (
            <SiteUrlStep onSubmit={onSiteUrlStepSubmitted} onSkip={onSiteUrlStepSkipped} />
          ) : null}
          {!isLoading && shouldShowInviteTeammateStep && step === STEPS.INVITE_TEAMMATE ? (
            <InviteTeammateStep onSkip={goToInsights} onSubmit={goToInsights} />
          ) : null}
          {!isLoading && currentSite && step === STEPS.INSTALLATION_METHOD ? (
            <StyledStep data-testid="installation-method">
              <InstallationMethodStep
                site={currentSite}
                detectedCms={cms}
                withGtm={gtm.detected}
                onNPMInstallClick={() => goToStep(STEPS.NPM)}
                onManualInstallClick={() => goToStep(STEPS.MANUAL_INSTALLATION)}
                onPlatformInstallClick={() => goToStep(STEPS.PLATFORMS)}
                onCmsInstallClick={onCMSClick}
                onShareCodeClick={() => goToStep(STEPS.SHARE_TRACKING_CODE)}
                goToStep={goToStep}
              />
            </StyledStep>
          ) : null}
          {!isLoading && step === STEPS.PLATFORMS ? (
            <StyledStep data-testid="platforms">
              <StepHeader
                currentStep={step}
                currentCms={cms}
                onBackClick={() => goToStep(STEPS.INSTALLATION_METHOD)}
              />
              <StepBox>
                <IntegrationGuides
                  goToStep={goToStep}
                  exclude={gtm.detected ? 'gtm' : cms}
                  onClick={onCMSClick}
                />
              </StepBox>
            </StyledStep>
          ) : null}
          {!isLoading && step === STEPS.SHARE_TRACKING_CODE ? (
            <StyledStep data-testid="share-tracking-code">
              <StepHeader
                currentStep={step}
                currentCms={cms}
                onBackClick={() => goToStep(STEPS.INSTALLATION_METHOD)}
              />
              <StepBox>
                <EmailTrackingCode />
              </StepBox>
            </StyledStep>
          ) : null}
          {!isLoading && step === STEPS.MANUAL_INSTALLATION ? (
            <StyledStep data-testid="manual-installation">
              <StepHeader
                currentStep={step}
                currentCms={cms}
                onBackClick={() => goToStep(STEPS.INSTALLATION_METHOD)}
              />
              <StepBox>
                <ManualInstallation site={currentSite as Site} />
              </StepBox>
            </StyledStep>
          ) : null}
          {!isLoading && step === STEPS.NPM ? (
            <StyledStep data-testid="npm-installation">
              <StepHeader
                currentStep={step}
                currentCms={cms}
                onBackClick={() => goToStep(STEPS.INSTALLATION_METHOD)}
              />
              <StepBox>
                <NPMInstallation site={currentSite as Site} />
              </StepBox>
            </StyledStep>
          ) : null}
          {!isLoading && step === STEPS.CMS ? (
            <StyledStep data-testid="cms">
              <StepHeader
                currentStep={step}
                currentCms={selectedCMS || cms}
                onBackClick={() => goToStep(STEPS.PLATFORMS)}
              />
              <StepBox>
                <IntegrateWithCMS cms={(selectedCMS as CMS) || cms} />
              </StepBox>
            </StyledStep>
          ) : null}
          {!isLoading && step === STEPS.GTM ? (
            <StyledStep data-testid="gtm">
              <StepHeader
                currentStep={step}
                currentCms={cms}
                onBackClick={() => goToStep(STEPS.INSTALLATION_METHOD)}
              />
              <StepBox>
                <IntegrateWithGTM
                  isInstalled={gtm.installed}
                  onBackClick={() => goToStep(STEPS.INSTALLATION_METHOD)}
                />
              </StepBox>
            </StyledStep>
          ) : null}
        </Content>
      </StyledRowWrapper>
    </Wrapper>
  );
};
